import React, { useState, useEffect } from "react";
import AdminSidebar from "../../components/AdminSidebar";
import "./admin.css";
import "./TowerAdminGateShifts.css";

export default function TowerAdminGateShifts() {
  // Shift creation form state
  const [shiftPin, setShiftPin] = useState("");
  const [shiftDate, setShiftDate] = useState(getTodayDate());
  const [shiftTimeMode, setShiftTimeMode] = useState("preset");
  const [shiftTimePreset, setShiftTimePreset] = useState("08:00-16:00");
  const [customStartTime, setCustomStartTime] = useState("08:00");
  const [customEndTime, setCustomEndTime] = useState("16:00");

  // Active shifts list
  const [activeShifts, setActiveShifts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Login history state
  const [historyRange, setHistoryRange] = useState("week");
  const [customFrom, setCustomFrom] = useState(getTodayDate());
  const [customTo, setCustomTo] = useState(getTodayDate());
  const [loginHistory, setLoginHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState("");
  const [historyCollapsed, setHistoryCollapsed] = useState(true);

  // Modal dialog state for deactivation
  const [showDeactivateModal, setShowDeactivateModal] = useState(false);
  const [deactivateShiftId, setDeactivateShiftId] = useState(null);
  const [selectedReason, setSelectedReason] = useState("");
  const [customReasonText, setCustomReasonText] = useState("");
  const [deactivateLoading, setDeactivateLoading] = useState(false);

  // Auth
  const adminToken = localStorage.getItem("token");

  // Load active shifts on mount
  useEffect(() => {
    if (!adminToken) {
      setError("Not authenticated");
      return;
    }
    loadActiveShifts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load login history when filters change
  useEffect(() => {
    if (!adminToken) return;
    loadLoginHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [historyRange, customFrom, customTo]);

  // ============================================================
  // UTILITY FUNCTIONS
  // ============================================================

  function getTodayDate() {
    const today = new Date();
    return today.toISOString().split("T")[0];
  }

  function toDateStr(d) {
    return d.toISOString().split("T")[0];
  }

  function getHistoryDateRange() {
    if (historyRange === "custom") {
      return { from: customFrom, to: customTo };
    }

    const today = new Date();
    const to = toDateStr(today);
    let from = to;

    if (historyRange === "week") {
      const start = new Date(today);
      start.setDate(start.getDate() - 6);
      from = toDateStr(start);
    } else if (historyRange === "month") {
      const start = new Date(today);
      start.setDate(start.getDate() - 29);
      from = toDateStr(start);
    }

    return { from, to };
  }

  // ============================================================
  // API CALLS
  // ============================================================

  const loadActiveShifts = async () => {
    try {
      setLoading(true);
      const API = process.env.REACT_APP_API_URL;
      const response = await fetch(`${API}/api/gate/shifts/active`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });

      if (!response.ok) throw new Error("Failed to fetch shifts");
      const data = await response.json();
      console.log("[LoadShifts] Loaded shifts from backend:", data.shifts);

      // Verify each shift has an id
      if (data.shifts && data.shifts.length > 0) {
        data.shifts.forEach((shift, idx) => {
          console.log(`[LoadShifts] Shift ${idx}:`, {
            id: shift.id,
            _id: shift._id,
            pin: shift.shiftPin,
            time: shift.shiftTime,
            date: shift.shiftDate
          });
        });
      }

      setActiveShifts(data.shifts || []);
    } catch (err) {
      console.error("Error loading shifts:", err);
      setError("Failed to load active shifts: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadLoginHistory = async () => {
    const { from, to } = getHistoryDateRange();
    if (!from || !to) return;

    try {
      setHistoryLoading(true);
      setHistoryError("");
      const API = process.env.REACT_APP_API_URL;
      const response = await fetch(
        `${API}/api/gate/login-history?from=${from}&to=${to}`,
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );

      if (!response.ok) throw new Error("Failed to fetch login history");
      const data = await response.json();
      setLoginHistory(data.logins || []);
    } catch (err) {
      console.error("Error loading gate login history:", err);
      setHistoryError("Failed to load login history: " + err.message);
    } finally {
      setHistoryLoading(false);
    }
  };

  const generateRandomPin = () => {
    const pin = Math.floor(100000 + Math.random() * 900000)
      .toString()
      .substring(0, 6);
    setShiftPin(pin);
  };

  // ============================================================
  // SHIFT CREATION
  // ============================================================

  const handleCreateShift = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    setSuccess("");

    try {
      if (!shiftPin.trim()) {
        throw new Error("Shift PIN is required");
      }

      if (shiftPin.trim().length < 4 || shiftPin.trim().length > 6) {
        throw new Error("Shift PIN must be 4-6 characters");
      }

      let startTime = "";
      let endTime = "";
      let displayTime = "";

      if (shiftTimeMode === "preset") {
        [startTime, endTime] = shiftTimePreset.split("-");
        displayTime = `${startTime}-${endTime}`;
      } else {
        startTime = customStartTime;
        endTime = customEndTime;
        displayTime = `${startTime}-${endTime}`;
      }

      const API = process.env.REACT_APP_API_URL;
      const response = await fetch(`${API}/api/gate/create-shift`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${adminToken}`
        },
        body: JSON.stringify({
          shiftPin: shiftPin.trim(),
          shiftDate: shiftDate,
          shiftStartTime: startTime,
          shiftEndTime: endTime,
          shiftTime: displayTime
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to create shift");
      }

      setSuccess("Shift PIN created successfully");
      setShiftPin("");
      setCustomStartTime("08:00");
      setCustomEndTime("16:00");
      loadActiveShifts();

      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      setError("Error: " + err.message);
    } finally {
      setSubmitting(false);
    }
  };

  // ============================================================
  // SHIFT DEACTIVATION
  // ============================================================

  const handleDeactivateShift = (shiftId) => {
    setDeactivateShiftId(shiftId);
    setShowDeactivateModal(true);
    setSelectedReason("");
    setCustomReasonText("");
  };

  const handleDeactivateConfirm = async () => {
    if (!selectedReason) {
      alert("Please select a reason");
      return;
    }

    if (selectedReason === "other" && !customReasonText.trim()) {
      alert("Please provide additional details");
      return;
    }

    setDeactivateLoading(true);
    setError("");

    try {
      if (!adminToken) {
        throw new Error("Authentication token missing. Please log in again.");
      }

      if (!deactivateShiftId) {
        throw new Error("Shift ID is missing. Please try again.");
      }

      // Find the shift object to get its shiftDate (needed for partition key)
      const shiftToDeactivate = activeShifts.find(s => (s.id || s._id) === deactivateShiftId);
      if (!shiftToDeactivate) {
        throw new Error("Shift not found in active shifts list");
      }

      const API = process.env.REACT_APP_API_URL;
      if (!API) {
        throw new Error("API URL not configured");
      }

      const deactivateUrl = `${API}/api/gate/shifts/${deactivateShiftId}/deactivate`;
      console.log("[DEBUG] Deactivating shift:", {
        shiftId: deactivateShiftId,
        shiftDate: shiftToDeactivate.shiftDate,
        reason: selectedReason,
        hasCustomReason: !!customReasonText,
        url: deactivateUrl,
        token: adminToken ? "present" : "missing"
      });

      const response = await fetch(deactivateUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${adminToken}`
        },
        body: JSON.stringify({
          shiftDate: shiftToDeactivate.shiftDate,
          reason: selectedReason,
          customReason: customReasonText || null
        })
      });

      console.log("[DEBUG] Deactivate response status:", response.status);

      if (!response.ok) {
        const errorData = await response
          .json()
          .catch(() => ({ message: `HTTP ${response.status}` }));
        console.error("[DEBUG] Deactivate error response:", errorData);
        throw new Error(
          errorData.message || `Failed to deactivate shift (${response.status})`
        );
      }

      const data = await response.json();
      console.log("[DEBUG] Shift deactivated successfully:", data);

      setSuccess("Shift deactivated successfully");
      setShowDeactivateModal(false);
      setDeactivateShiftId(null);
      setSelectedReason("");
      setCustomReasonText("");

      setTimeout(() => loadActiveShifts(), 500);
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      console.error("[DEBUG] Deactivation error:", err);
      setError("Failed to deactivate shift: " + err.message);
    } finally {
      setDeactivateLoading(false);
    }
  };

  const handleDeactivateCancel = () => {
    setShowDeactivateModal(false);
    setDeactivateShiftId(null);
    setSelectedReason("");
    setCustomReasonText("");
  };

  // ============================================================
  // RENDER
  // ============================================================

  return (
    <div className="admin-layout">
      <AdminSidebar />
      <div className="admin-content">
        <div className="gate-shifts-container">
          {/* Header */}
          <div className="shifts-header">
            <h1>Gate Shift PIN Management</h1>
            <p>Generate daily shift codes for gate staff</p>
          </div>

          {/* Alerts */}
          {error && <div className="alert alert-error">{error}</div>}
          {success && <div className="alert alert-success">{success}</div>}

          <div className="shifts-content">
            {/* Create Shift Form */}
            <div className="shift-form-section">
              <h2>Generate New Shift PIN</h2>

              <form onSubmit={handleCreateShift} className="shift-form">
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="shiftDate">Shift Date</label>
                    <input
                      type="date"
                      id="shiftDate"
                      value={shiftDate}
                      onChange={(e) => setShiftDate(e.target.value)}
                      className="form-input"
                      required
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Shift Time</label>
                  <div className="shift-time-selector">
                    <label className="radio-option">
                      <input
                        type="radio"
                        name="shiftTimeMode"
                        value="preset"
                        checked={shiftTimeMode === "preset"}
                        onChange={() => setShiftTimeMode("preset")}
                      />
                      <span>Preset Times</span>
                    </label>
                    <label className="radio-option">
                      <input
                        type="radio"
                        name="shiftTimeMode"
                        value="custom"
                        checked={shiftTimeMode === "custom"}
                        onChange={() => setShiftTimeMode("custom")}
                      />
                      <span>Custom Times</span>
                    </label>
                  </div>
                </div>

                {shiftTimeMode === "preset" ? (
                  <div className="form-group">
                    <label htmlFor="shiftTimePreset">Select Time Slot</label>
                    <select
                      id="shiftTimePreset"
                      value={shiftTimePreset}
                      onChange={(e) => setShiftTimePreset(e.target.value)}
                      className="form-input"
                    >
                      <option value="08:00-16:00">8:00 AM - 4:00 PM</option>
                      <option value="16:00-00:00">4:00 PM - 12:00 AM</option>
                      <option value="00:00-08:00">12:00 AM - 8:00 AM</option>
                    </select>
                  </div>
                ) : (
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="customStartTime">Start Time</label>
                      <input
                        type="time"
                        id="customStartTime"
                        value={customStartTime}
                        onChange={(e) => setCustomStartTime(e.target.value)}
                        className="form-input"
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="customEndTime">End Time</label>
                      <input
                        type="time"
                        id="customEndTime"
                        value={customEndTime}
                        onChange={(e) => setCustomEndTime(e.target.value)}
                        className="form-input"
                        required
                      />
                    </div>
                  </div>
                )}

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="shiftPin">Shift PIN</label>
                    <div style={{ display: "flex", gap: "8px" }}>
                      <input
                        type="text"
                        id="shiftPin"
                        value={shiftPin}
                        onChange={(e) => setShiftPin(e.target.value)}
                        className="form-input"
                        placeholder="Or generate one"
                        maxLength="6"
                        required
                      />
                      <button
                        type="button"
                        onClick={generateRandomPin}
                        className="btn-secondary"
                      >
                        Generate
                      </button>
                    </div>
                  </div>
                </div>

                <button
                  type="submit"
                  className="btn-primary"
                  disabled={submitting}
                >
                  {submitting ? "Creating..." : "Create Shift"}
                </button>
              </form>
            </div>

            {/* Active Shifts Table */}
            <div className="active-shifts-section">
              <h2>Today's Active Shifts ({activeShifts.length})</h2>

              {loading ? (
                <div className="loading">Loading active shifts...</div>
              ) : activeShifts.length === 0 ? (
                <div className="empty-state">
                  <p>No active shifts for today</p>
                  <p className="small">Create one above to get started</p>
                </div>
              ) : (
                <div className="shifts-table">
                  <table>
                    <thead>
                      <tr>
                        <th>Shift Time</th>
                        <th>PIN Code</th>
                        <th>Created By</th>
                        <th>Created At</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {activeShifts.map((shift) => {
                        const shiftId = shift.id || shift._id;
                        return (
                          <tr key={shiftId}>
                            <td>{shift.shiftTime}</td>
                            <td className="pin-code">{shift.shiftPin}</td>
                            <td>{shift.createdBy}</td>
                            <td>
                              {new Date(shift.createdAt).toLocaleTimeString(
                                "en-IN"
                              )}
                            </td>
                            <td>
                              <button
                                className="btn-deactivate"
                                onClick={() => {
                                  console.log("[Table] Deactivating shift:", {
                                    shiftId,
                                    pin: shift.shiftPin,
                                    time: shift.shiftTime
                                  });
                                  handleDeactivateShift(shiftId);
                                }}
                              >
                                End
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Gate Staff Login History - Collapsible */}
            <div className="active-shifts-section">
              <div
                className="history-collapse-toggle"
                onClick={() => setHistoryCollapsed(!historyCollapsed)}
              >
                <span
                  className={`history-collapse-icon ${
                    historyCollapsed ? "collapsed" : ""
                  }`}
                >
                  ▼
                </span>
                <h2 style={{ margin: 0, display: "inline", fontSize: "inherit" }}>
                  Gate Staff Login History ({loginHistory.length})
                </h2>
              </div>

              <div className={`history-content ${historyCollapsed ? "collapsed" : ""}`}>
                <div className="history-filters">
                  <div className="history-range-tabs">
                    {[
                      { key: "day", label: "Today" },
                      { key: "week", label: "This Week" },
                      { key: "month", label: "This Month" },
                      { key: "custom", label: "Custom" }
                    ].map((opt) => (
                      <button
                        key={opt.key}
                        type="button"
                        className={
                          "history-range-tab" +
                          (historyRange === opt.key ? " active" : "")
                        }
                        onClick={() => setHistoryRange(opt.key)}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>

                  {historyRange === "custom" && (
                    <div className="history-custom-range">
                      <div className="form-group">
                        <label htmlFor="historyFrom">From</label>
                        <input
                          type="date"
                          id="historyFrom"
                          className="form-input"
                          value={customFrom}
                          max={customTo}
                          onChange={(e) => setCustomFrom(e.target.value)}
                        />
                      </div>
                      <div className="form-group">
                        <label htmlFor="historyTo">To</label>
                        <input
                          type="date"
                          id="historyTo"
                          className="form-input"
                          value={customTo}
                          min={customFrom}
                          max={getTodayDate()}
                          onChange={(e) => setCustomTo(e.target.value)}
                        />
                      </div>
                    </div>
                  )}
                </div>

                {historyError && (
                  <div className="alert alert-error">{historyError}</div>
                )}

                {historyLoading ? (
                  <div className="loading">Loading login history...</div>
                ) : loginHistory.length === 0 ? (
                  <div className="empty-state">
                    <p>No gate staff logins in this range</p>
                    <p className="small">Try widening the date range above</p>
                  </div>
                ) : (
                  <div className="shifts-table">
                    <table>
                      <thead>
                        <tr>
                          <th>Date &amp; Time</th>
                          <th>Staff Name</th>
                          <th>Login Method</th>
                          <th>Shift / Tower</th>
                        </tr>
                      </thead>
                      <tbody>
                        {loginHistory.map((log) => (
                          <tr key={log.id}>
                            <td>
                              {new Date(log.loginAt).toLocaleString("en-IN")}
                            </td>
                            <td>{log.staffName}</td>
                            <td>
                              <span
                                className={`history-method-badge ${
                                  log.method === "shift" ? "shift" : "staff"
                                }`}
                              >
                                {log.method === "shift"
                                  ? "Shift PIN"
                                  : "Staff Login"}
                              </span>
                            </td>
                            <td>
                              {log.method === "shift"
                                ? log.shiftTime || "N/A"
                                : `Tower ${log.tower || "N/A"}`}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Deactivation Reason Modal */}
          {showDeactivateModal && (
            <div
              className="modal-overlay"
              onClick={handleDeactivateCancel}
            >
              <div
                className="modal-dialog"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="modal-header">
                  <h3>Why are you ending this shift early?</h3>
                </div>
                <div className="modal-body">
                  <div className="reason-options">
                    {[
                      {
                        value: "staff-left-early",
                        label: "Staff member left early"
                      },
                      {
                        value: "emergency",
                        label: "Emergency/security incident"
                      },
                      { value: "fewer-visitors", label: "Fewer visitors expected" },
                      { value: "other", label: "Other (specify below)" }
                    ].map((option) => (
                      <label key={option.value} className="reason-radio">
                        <input
                          type="radio"
                          name="deactivateReason"
                          value={option.value}
                          checked={selectedReason === option.value}
                          onChange={(e) => {
                            setSelectedReason(e.target.value);
                            if (e.target.value !== "other") {
                              setCustomReasonText("");
                            }
                          }}
                        />
                        <span>{option.label}</span>
                      </label>
                    ))}
                  </div>

                  {selectedReason === "other" && (
                    <div className="form-group" style={{ marginTop: "16px" }}>
                      <label htmlFor="customReason">Additional Details</label>
                      <textarea
                        id="customReason"
                        className="form-input"
                        rows="3"
                        value={customReasonText}
                        onChange={(e) => setCustomReasonText(e.target.value)}
                        placeholder="Please explain..."
                      />
                    </div>
                  )}
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn-cancel"
                    onClick={handleDeactivateCancel}
                    disabled={deactivateLoading}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn-deactivate-confirm"
                    onClick={handleDeactivateConfirm}
                    disabled={!selectedReason || deactivateLoading}
                  >
                    {deactivateLoading ? "Ending..." : "End Shift"}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}